import { Avatar, Inline, Logo, NavItem, NavSection, Sheet, Skeleton, Stack, surface, useIsMobile } from "@nudgebox/ui";
import { AppsIcon, BillingIcon, DocsIcon, HomeIcon, KeyIcon, MenuIcon, PlusIcon, SettingsIcon, UsersIcon } from "@nudgebox/ui/icons";
import "@nudgebox/ui/tokens.stylex";
import { color, font, fontWeight, media, motion, radius, space, zIndex } from "@nudgebox/ui/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { cloneElement, type ReactElement, type ReactNode, useState } from "react";
import { PendingOutlet } from "@/app/route-pending.tsx";
import { useOrgSlug } from "@/lib/params.ts";
import { applicationsQuery } from "@/lib/queries.ts";
import { CommandMenu } from "./CommandMenu.tsx";
import { InstallAppDialog } from "./InstallAppDialog.tsx";
import { OrganizationSwitcher } from "./OrganizationSwitcher.tsx";
import { UserMenu } from "./UserMenu.tsx";

/**
 * Organization shell. Desktop gets a persistent sidebar; phones get native
 * app chrome instead — a compact top bar, a bottom tab bar for the main
 * destinations and a swipeable sheet holding the full navigation.
 */
export function AppShell() {
  const isMobile = useIsMobile();
  return <>
      {isMobile ? <MobileShell /> : <DesktopShell />}
      {/* Phones and tablets only; renders nothing on desktop or once installed. */}
      <InstallAppDialog />
    </>;
}

/**
 * The shell's own loading state (organization, its apps and the user still on
 * the way): the same chrome with its contents stubbed out, and the skeleton of
 * the page being opened in the content column.
 */
export function AppShellPending() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileShellPending /> : <DesktopShellPending />;
}
function DesktopShellPending() {
  return <div aria-busy className="x78zum5 xg6iff7 xvccuex">
      <aside className="x7wzq59 x13vifvy x1dr59a3 x1t9vceh x2lah0s x78zum5 xh6stid x14nik9e x1kl24ur">
        <div {...stylex.props(surface.raised, surface.md, styles.sidebarCard)}>
          <div className="x1iyjqo2 x2lwn1j x78zum5 xdt5ytf xpcbtbh x549ohs x1l9m7az xsef1i0 x1odjw0f x1597r2g x114bzrq x9gk5ev">
            <div className="x1uu5im9 x18dy136">
              <Logo />
            </div>
            <Skeleton height={36} radius="md" />
            <Skeleton height={30} radius="md" />
            <SidebarNavPending />
          </div>
          <div className="x2lah0s x1kpc7t0 x178xt8z x13fuv20 x8yrp0t">
            <Inline gap="sm">
              <Skeleton width={28} height={28} radius="full" />
              <Stack gap="xs" grow>
                <Skeleton width="62%" height={12} />
                <Skeleton width="84%" height={10} />
              </Stack>
            </Inline>
          </div>
        </div>
      </aside>

      <main className="x1iyjqo2 xeuugli xvccuex">
        <div className="x4gyc16 xvueqy4 x7xzu7j x1f3vwq8 xme3qm3 xfs5dke">
          <PendingOutlet />
        </div>
      </main>
    </div>;
}
function MobileShellPending() {
  return <div aria-busy className="x1ov3xa9 xvccuex">
      <header className="x7wzq59 x13vifvy xl95b3i x78zum5 x6s0dn4 xjenv9e x159yxtc x1mrd0q x159mqhl xie7x17 xbxmz36 xt7zdng x1lm297j xso031l x1q0q8m5 x13szane">
        <div className="x1iyjqo2 xeuugli">
          <Inline gap="sm">
            <Skeleton width={24} height={24} radius="md" />
            <Skeleton width={120} height={14} />
          </Inline>
        </div>
        <Skeleton width={32} height={32} radius="md" />
        <Skeleton width={32} height={32} radius="full" />
      </header>

      <main className="x156km83 x1otn9g0 x1gv4wwb xlm2typ">
        <PendingOutlet />
      </main>

      <nav aria-hidden className="xixxii4 x17y0mx6 x1ey2m1c xl95b3i xrvj5dj x1mt1orb x1rnidkh x1xoqpw8 x1a148e8 x196z7tn xg4jtgg xox9xce xt7zdng x1lm297j x178xt8z x13fuv20 x1fwp53n">
        {Array.from({
        length: 5
      }, (_, index) => <span key={index} className="x78zum5 xdt5ytf x6s0dn4 xl56j7k x1ed6fcf xeuugli x1717udv xc342km xjbqb8w xxe3v3u x1hl2dhg x1ypdohk x87ps6o xpdipgo x1a2a7pz x2m66ac x7updtu xyda15a x1r0vdo7">
            <Skeleton width={22} height={22} radius="md" />
            <Skeleton width={36} height={8} />
          </span>)}
      </nav>
    </div>;
}

/** Nav sections as the user will see them; item labels vary in length like real names. */
function SidebarNavPending() {
  const sections = [{
    label: null,
    items: [56]
  }, {
    label: 64,
    items: [72, 58, 84]
  }, {
    label: 52,
    items: [60, 88]
  }, {
    label: 80,
    items: [62, 48, 56]
  }];
  return <div className="x78zum5 xdt5ytf x4l6xbm x1iyjqo2">
      {sections.map((section, index) => <div key={index} className="x78zum5 xdt5ytf x12mrbbr">
          {section.label ? <div className="x1uu5im9 x2sz766 x18dy136">
              <Skeleton width={section.label} height={10} />
            </div> : null}
          {section.items.map((width, item) => <div key={item} className="x78zum5 x6s0dn4 x1a62zme x1gnnpzl x8tjdam x1uu5im9">
              <Skeleton width={15} height={15} radius="sm" />
              <Skeleton width={`${width}%`} height={12} />
            </div>)}
        </div>)}
    </div>;
}
function useIsActive() {
  const orgSlug = useOrgSlug();
  const matchRoute = useMatchRoute();
  return (to: string, fuzzy = false) => Boolean(matchRoute({
    to,
    params: {
      orgSlug
    },
    fuzzy
  }));
}
function DesktopShell() {
  const orgSlug = useOrgSlug();
  return <div className="x78zum5 xg6iff7 xvccuex">
      <aside className="x7wzq59 x13vifvy x1dr59a3 x1t9vceh x2lah0s x78zum5 xh6stid x14nik9e x1kl24ur">
        {/* A detached raised card floating in the page gutter. The scroller
            lives inside it so `overflow` never clips the slab. */}
        <div {...stylex.props(surface.raised, surface.md, styles.sidebarCard)}>
          <div className="x1iyjqo2 x2lwn1j x78zum5 xdt5ytf xpcbtbh x549ohs x1l9m7az xsef1i0 x1odjw0f x1597r2g x114bzrq x9gk5ev">
            <div className="x1uu5im9 x18dy136">
              <Logo />
            </div>
            <OrganizationSwitcher currentSlug={orgSlug} />
            <CommandMenu orgSlug={orgSlug} />
            <SidebarNav />
          </div>
          <div className="x2lah0s x1kpc7t0 x178xt8z x13fuv20 x8yrp0t">
            <UserMenu />
          </div>
        </div>
      </aside>

      <main className="x1iyjqo2 xeuugli xvccuex">
        <div className="x4gyc16 xvueqy4 x7xzu7j x1f3vwq8 xme3qm3 xfs5dke">
          <Outlet />
        </div>
      </main>
    </div>;
}
function MobileShell() {
  const orgSlug = useOrgSlug();
  const is = useIsActive();
  const [menuOpen, setMenuOpen] = useState(false);
  const params = {
    orgSlug
  };
  return <div className="x1ov3xa9 xvccuex">
      <header className="x7wzq59 x13vifvy xl95b3i x78zum5 x6s0dn4 xjenv9e x159yxtc x1mrd0q x159mqhl xie7x17 xbxmz36 xt7zdng x1lm297j xso031l x1q0q8m5 x13szane">
        <div className="x1iyjqo2 xeuugli">
          <OrganizationSwitcher currentSlug={orgSlug} />
        </div>
        <CommandMenu orgSlug={orgSlug} variant="icon" />
        <UserMenu compact />
      </header>

      <main className="x156km83 x1otn9g0 x1gv4wwb xlm2typ">
        <Outlet />
      </main>

      <nav aria-label="Main" className="xixxii4 x17y0mx6 x1ey2m1c xl95b3i xrvj5dj x1mt1orb x1rnidkh x1xoqpw8 x1a148e8 x196z7tn xg4jtgg xox9xce xt7zdng x1lm297j x178xt8z x13fuv20 x1fwp53n">
        <TabBarItem render={<Link to="/$orgSlug" params={params} />} icon={<HomeIcon size={22} />} label="Overview" active={is("/$orgSlug")} />
        <TabBarItem render={<Link to="/$orgSlug/apps" params={params} />} icon={<AppsIcon size={22} />} label="Apps" active={is("/$orgSlug/apps", true)} />
        <TabBarItem render={<Link to="/$orgSlug/settings/api-keys" params={params} />} icon={<KeyIcon size={22} />} label="API keys" active={is("/$orgSlug/settings/api-keys")} />
        <TabBarItem render={<Link to="/$orgSlug/developers" params={params} />} icon={<DocsIcon size={22} />} label="Docs" active={is("/$orgSlug/developers")} />
        <button type="button" aria-haspopup="dialog" onClick={() => setMenuOpen(true)} {...{
        0: {
          className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k x1ed6fcf xeuugli x1717udv xc342km xjbqb8w xxe3v3u x1hl2dhg x1ypdohk x87ps6o xpdipgo x1a2a7pz x2m66ac x7updtu xyda15a x1r0vdo7"
        },
        1: {
          className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k x1ed6fcf xeuugli x1717udv xc342km xjbqb8w x1hl2dhg x1ypdohk x87ps6o xpdipgo x1a2a7pz x2m66ac x7updtu xyda15a x1r0vdo7 x9aqtqa"
        }
      }[!!(menuOpen || is("/$orgSlug/settings") || is("/$orgSlug/settings/account") || is("/$orgSlug/settings/members") || is("/$orgSlug/settings/billing")) << 0]}>
          <TabBarContent icon={<MenuIcon size={22} />} label="More" />
        </button>
      </nav>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen} title="Menu" hideHeader>
        <div className="x14ur6ox">
          {/* Any link tap dismisses the sheet, including the current page's. */}
          <SidebarNav label="All pages" onNavigate={() => setMenuOpen(false)} />
        </div>
      </Sheet>
    </div>;
}
function SidebarNav({
  label = "Main",
  onNavigate
}: {
  label?: string;
  onNavigate?: () => void;
}) {
  const orgSlug = useOrgSlug();
  const matchRoute = useMatchRoute();
  const is = useIsActive();
  const {
    data: applications
  } = useSuspenseQuery(applicationsQuery(orgSlug));
  const params = {
    orgSlug
  };
  return <nav aria-label={label} className="x78zum5 xdt5ytf x4l6xbm x1iyjqo2">
      <NavSection>
        <NavItem render={<Link to="/$orgSlug" params={params} onClick={onNavigate} />} icon={<HomeIcon size={15} />} active={is("/$orgSlug")}>
          Overview
        </NavItem>
      </NavSection>

      <NavSection label="Applications">
        {applications.map(app => <NavItem key={app.id} render={<Link to="/$orgSlug/apps/$appSlug" params={{
        orgSlug,
        appSlug: app.slug
      }} onClick={onNavigate} />} icon={<Avatar name={app.name} src={app.iconUrl} size={16} square />} active={Boolean(matchRoute({
        to: "/$orgSlug/apps/$appSlug",
        params: {
          orgSlug,
          appSlug: app.slug
        },
        fuzzy: true
      }))}>
            {app.name}
          </NavItem>)}
        <NavItem render={<Link to="/$orgSlug/apps" params={params} onClick={onNavigate} />} icon={applications.length ? <AppsIcon size={15} /> : <PlusIcon size={15} />} active={is("/$orgSlug/apps")}>
          {applications.length ? "All applications" : "Create application"}
        </NavItem>
      </NavSection>

      <NavSection label="Developer">
        <NavItem render={<Link to="/$orgSlug/settings/api-keys" params={params} onClick={onNavigate} />} icon={<KeyIcon size={15} />} active={is("/$orgSlug/settings/api-keys")}>
          API keys
        </NavItem>
        <NavItem render={<Link to="/$orgSlug/developers" params={params} onClick={onNavigate} />} icon={<DocsIcon size={15} />} active={is("/$orgSlug/developers")}>
          Documentation
        </NavItem>
      </NavSection>

      <NavSection label="Organization">
        <NavItem render={<Link to="/$orgSlug/settings/members" params={params} onClick={onNavigate} />} icon={<UsersIcon size={15} />} active={is("/$orgSlug/settings/members")}>
          Members
        </NavItem>
        <NavItem render={<Link to="/$orgSlug/settings/billing" params={params} search={{}} onClick={onNavigate} />} icon={<BillingIcon size={15} />} active={is("/$orgSlug/settings/billing")}>
          Billing
        </NavItem>
        <NavItem render={<Link to="/$orgSlug/settings" params={params} onClick={onNavigate} />} icon={<SettingsIcon size={15} />} active={is("/$orgSlug/settings") || is("/$orgSlug/settings/account")}>
          Settings
        </NavItem>
      </NavSection>
    </nav>;
}

/** Bottom tab bar link. */
function TabBarItem({
  render,
  icon,
  label,
  active
}: {
  render: ReactElement<{
    className?: string;
    children?: ReactNode;
    "aria-current"?: "page";
  }>;
  icon: ReactNode;
  label: string;
  active: boolean;
}) {
  return cloneElement(render, {
    "aria-current": active ? "page" : undefined,
    ...{
      0: {
        className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k x1ed6fcf xeuugli x1717udv xc342km xjbqb8w xxe3v3u x1hl2dhg x1ypdohk x87ps6o xpdipgo x1a2a7pz x2m66ac x7updtu xyda15a x1r0vdo7"
      },
      1: {
        className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k x1ed6fcf xeuugli x1717udv xc342km xjbqb8w x1hl2dhg x1ypdohk x87ps6o xpdipgo x1a2a7pz x2m66ac x7updtu xyda15a x1r0vdo7 x9aqtqa"
      }
    }[!!active << 0],
    children: <TabBarContent icon={icon} label={label} />
  });
}
function TabBarContent({
  icon,
  label
}: {
  icon: ReactNode;
  label: string;
}) {
  return <>
      <span className="x78zum5">{icon}</span>
      <span className="x193iq5w xb3r6kr xlyipyv xuxw1ft x1wv6yj x1s31vzs x1219144 x1yf5rgg">{label}</span>
    </>;
}
const TOP_BAR_HEIGHT = 52;
const TAB_BAR_HEIGHT = 56;
const SAFE_TOP = "env(safe-area-inset-top, 0px)";
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";
const SAFE_LEFT = "env(safe-area-inset-left, 0px)";
const SAFE_RIGHT = "env(safe-area-inset-right, 0px)";
/** Matches `surface.md`'s slab offset, so the slab clears the viewport edge. */
const SLAB_OFFSET = "6px";
const styles = {
  sidebarCard: {
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xeuugli",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kaIpWk: "x16l8lxw",
    $$css: true
  }
};